"use client";

/**
 * React hook for analytics tracking
 *
 * Provides convenient access to all tracking functions
 * with memoization to prevent unnecessary re-renders.
 */

import { useCallback, useMemo, useRef } from "react";

import type {
  AddPaymentInfoParams,
  AnalysisCompleteParams,
  AnalysisStartParams,
  BeginCheckoutParams,
  ChatMessageSendParams,
  ChatShareParams,
  ChatStartParams,
  CtaClickParams,
  FormStartParams,
  FormSubmitParams,
  GenerateLeadParams,
  LoginParams,
  NewsletterSignupParams,
  Placement,
  PurchaseParams,
  ShareParams,
  SignUpParams,
  UpgradeViewParams,
} from "./events";
import {
  clearUserProperties,
  isGtmConfigured,
  setUserProperties,
  trackAddPaymentInfo,
  trackAnalysisComplete,
  trackAnalysisStart,
  trackBeginCheckout,
  trackChatMessageSend,
  trackChatShare,
  trackChatStart,
  trackCtaClick,
  trackFormStart,
  trackFormSubmit,
  trackGenerateLead,
  trackLogin,
  trackNewsletterSignup,
  trackPurchase,
  trackShare,
  trackSignUp,
  trackUpgradeView,
} from "./gtm";

// =============================================================================
// Main Analytics Hook
// =============================================================================

export type UseAnalyticsReturn = {
  // Core tracking functions
  trackCtaClick: (params: CtaClickParams) => void;
  trackFormStart: (params: FormStartParams) => void;
  trackFormSubmit: (params: FormSubmitParams) => void;
  trackGenerateLead: (params: GenerateLeadParams) => void;
  trackNewsletterSignup: (params: NewsletterSignupParams) => void;
  trackBeginCheckout: (params: BeginCheckoutParams) => void;
  trackAddPaymentInfo: (params: AddPaymentInfoParams) => void;
  trackPurchase: (params: PurchaseParams) => void;
  trackUpgradeView: (params: UpgradeViewParams) => void;
  trackAnalysisStart: (params: AnalysisStartParams) => void;
  trackAnalysisComplete: (params: AnalysisCompleteParams) => void;
  trackShare: (params: ShareParams) => void;
  trackChatStart: (params: ChatStartParams) => void;
  trackChatMessageSend: (params: ChatMessageSendParams) => void;
  trackChatShare: (params: ChatShareParams) => void;
  trackSignUp: (params: SignUpParams) => void;
  trackLogin: (params: LoginParams) => void;

  // User properties
  setUserProperties: (properties: {
    user_id?: string;
    user_tier?: string;
    is_guest?: boolean;
  }) => void;
  clearUserProperties: () => void;

  // Utility
  isConfigured: boolean;
};

/**
 * Hook providing access to all analytics tracking functions.
 * Functions are memoized to prevent unnecessary re-renders.
 */
export function useAnalytics(): UseAnalyticsReturn {
  return useMemo(
    () => ({
      trackCtaClick,
      trackFormStart,
      trackFormSubmit,
      trackGenerateLead,
      trackNewsletterSignup,
      trackBeginCheckout,
      trackAddPaymentInfo,
      trackPurchase,
      trackUpgradeView,
      trackAnalysisStart,
      trackAnalysisComplete,
      trackShare,
      trackChatStart,
      trackChatMessageSend,
      trackChatShare,
      trackSignUp,
      trackLogin,
      setUserProperties,
      clearUserProperties,
      isConfigured: isGtmConfigured(),
    }),
    []
  );
}

// =============================================================================
// Form Tracking Hook
// =============================================================================

export type UseFormTrackingOptions = {
  formId: string;
  formType: string;
  placement: Placement | string;
};

export type UseFormTrackingReturn = {
  handleFocus: () => void;
  handleSubmit: (success: boolean) => void;
  resetFormTracking: () => void;
};

/**
 * Hook for tracking form interactions.
 * Fires form_start only once per form instance.
 */
export function useFormTracking(
  options: UseFormTrackingOptions
): UseFormTrackingReturn {
  const formStarted = useRef(false);
  const { formId, formType, placement } = options;

  const handleFocus = useCallback(() => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackFormStart({
        form_id: formId,
        form_type: formType,
        placement,
      });
    }
  }, [formId, formType, placement]);

  const handleSubmit = useCallback(
    (success: boolean) => {
      trackFormSubmit({
        form_id: formId,
        form_type: formType,
        placement,
        success,
      });
    },
    [formId, formType, placement]
  );

  const resetFormTracking = useCallback(() => {
    formStarted.current = false;
  }, []);

  return {
    handleFocus,
    handleSubmit,
    resetFormTracking,
  };
}

// =============================================================================
// Checkout Tracking Hook
// =============================================================================

export type UseCheckoutTrackingOptions = {
  tier: string;
  value: number;
  currency?: string;
  domain?: string;
};

export type UseCheckoutTrackingReturn = {
  trackCheckoutStart: () => void;
  trackPaymentInfo: (paymentMethod: string) => void;
  trackPurchaseComplete: (transactionId: string) => void;
};

/**
 * Hook for tracking checkout funnel.
 * Ensures begin_checkout is only tracked once.
 */
export function useCheckoutTracking(
  options: UseCheckoutTrackingOptions
): UseCheckoutTrackingReturn {
  const checkoutTracked = useRef(false);
  const { tier, value, currency = "USD", domain } = options;

  const trackCheckoutStart = useCallback(() => {
    if (!checkoutTracked.current) {
      checkoutTracked.current = true;
      trackBeginCheckout({
        tier,
        value,
        currency,
        domain,
      });
    }
  }, [tier, value, currency, domain]);

  const trackPaymentInfo = useCallback(
    (paymentMethod: string) => {
      trackAddPaymentInfo({
        tier,
        value,
        currency,
        payment_method: paymentMethod,
      });
    },
    [tier, value, currency]
  );

  const trackPurchaseComplete = useCallback(
    (transactionId: string) => {
      trackPurchase({
        transaction_id: transactionId,
        tier,
        value,
        currency,
      });
    },
    [tier, value, currency]
  );

  return {
    trackCheckoutStart,
    trackPaymentInfo,
    trackPurchaseComplete,
  };
}
